import React, { useState, useEffect } from "react";
import {
  getTemplateGuidelines,
  getTemplates,
  getTemplateVariables,
  registerTemplateActor,
  uploadBackgroundAsset,
} from "../../client/api";
import EditIcon from "@mui/icons-material/Edit";
import FileUploadIcon from "@mui/icons-material/FileUpload";
import MovieIcon from "@mui/icons-material/Movie";
import InfoIcon from "@mui/icons-material/Info";
import CollectionsIcon from "@mui/icons-material/Collections";
import TemplateGuidelinesDialog from "./components/TemplateGuidelinesDialog";
import {
  Modal,
  Box,
  Button,
  Typography,
  IconButton,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Divider,
  TextField,
  FormControl,
  FormLabel,
  RadioGroup,
  Radio,
  Select,
  MenuItem,
  InputLabel,
  Checkbox,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import {
  Actor,
  HeygenTemplate,
  TemplateGuidelines,
  TemplateVariable,
  User,
} from "../../context/types";
import { THEME_COLORS } from "../../context/constants";
import { WhiteTextField } from "../WhiteTextField";
import useStyles from "../../utils/styles";
import MediaUploadModal from "../MediaUploadModal";
import AssetLibraryModal from "../AssetLibraryModal";
import { useUserContext } from "../../context/UserContext";
import { useProjectContext } from "../../context/ProjectContext";
import ActorsDialog from "../ActorsDialog";

const modalStyle = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: "80%",
  maxWidth: "900px",
  maxHeight: "80vh",
  bgcolor: "background.paper",
  boxShadow: 24,
  borderRadius: "8px",
  overflow: "hidden",
  display: "flex",
  flexDirection: "column",
};

interface VariableValues {
  [key: string]: any;
}

const TemplateDialog = ({
  open,
  onClose,
  workspaceId,
  videoPageId,
  onTemplateAdded,
  onApplyTemplate,
  isLoading = false,
  actors,
  isSingleActorMode = false,
}: {
  open: boolean;
  onClose: () => void;
  workspaceId: string;
  videoPageId: string;
  onTemplateAdded?: (templateActor: any) => void;
  onApplyTemplate?: (
    templateId: string,
    actorIds: string[],
    variables: any,
    selectedActors?: Actor[],
    perActorVariables?: { [actorId: string]: any }
  ) => void;
  isLoading?: boolean;
  actors: Actor[];
  isSingleActorMode?: boolean;
}) => {
  const globalClasses = useStyles();
  const { user } = useUserContext();
  const [templates, setTemplates] = useState<HeygenTemplate[]>([]);
  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState(0); // 0: choose template, 1: configure variables
  const [selectedTemplate, setSelectedTemplate] = useState<HeygenTemplate | null>(null);
  const [templateVariables, setTemplateVariables] = useState<any>({});
  const [variableValues, setVariableValues] = useState<VariableValues>({});
  const [guidelinesOpen, setGuidelinesOpen] = useState(false);
  const [guidelines, setGuidelines] = useState<TemplateGuidelines>();
  const [loadingGuidelines, setLoadingGuidelines] = useState(false);
  const [applying, setApplying] = useState(false);
  const [useDefaultActor, setUseDefaultActor] = useState(false);
  const [selectedActors, setSelectedActors] = useState<Actor[]>(actors);
  const [hasActorVariables, setHasActorVariables] = useState(false);
  const [imageUploadModalOpen, setMediaUploadModalOpen] = useState(false);
  const [assetLibraryOpen, setAssetLibraryOpen] = useState(false);
  const { currentScript, updateScriptContent } = useProjectContext();
  const [currentImageVariable, setCurrentImageVariable] = useState<string | null>(null);
  const [mediaType, setMediaType] = useState<"video" | "image">("image");
  const [uploadingImage, setUploadingImage] = useState(false);
  const [missingScriptVariables, setMissingScriptVariables] = useState({
    showHeadline: false,
    showBody: false,
    showCta: false,
  });
  const [isActorsDialogOpen, setIsActorsDialogOpen] = useState(false);
  const [pendingTemplateApplication, setPendingTemplateApplication] = useState(false);
  const [templateFallbackImages, setTemplateFallbackImages] = useState<{
    [templateId: string]: string;
  }>({});

  // Reset dialog state only when modal opens
  useEffect(() => {
    if (open) {
      fetchTemplates();
      setStep(0);
      setSelectedTemplate(null);
      setVariableValues({});
      setUseDefaultActor(false);
      setSelectedActors(actors);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const fetchTemplates = async () => {
    try {
      setLoading(true);
      const templates = await getTemplates();
      setTemplates(templates);

      // Fetch guidelines for templates without thumbnails to get fallback images
      const templatesWithoutThumbnails = templates.filter(
        (t: HeygenTemplate) => !t.thumbnail_image_url
      );

      const fallbackImages: { [templateId: string]: string } = {};
      await Promise.all(
        templatesWithoutThumbnails.map(async (template: HeygenTemplate) => {
          try {
            const response = await getTemplateGuidelines(template.template_id);
            if (response.data?.imageExample) {
              fallbackImages[template.template_id] = response.data.imageExample;
            }
          } catch (error) {
            // Silently fail - we'll show the default placeholder
          }
        })
      );
      setTemplateFallbackImages(fallbackImages);
    } catch (error) {
      console.error("Failed to fetch templates:", error);
    } finally {
      setLoading(false);
    }
  };
  const fetchTemplateVariables = async (templateId: string) => {
    try {
      setLoading(true);
      const variables = await getTemplateVariables(templateId);

      // Sort variables immediately after fetching
      const sortedVariables = Object.fromEntries(
        Object.entries(variables).sort(([_, a]: any, [__, b]: any) => {
          const aName = a.name.toLowerCase();
          const bName = b.name.toLowerCase();

          if (aName.includes("hook") || aName.includes("headline")) return -1;
          if (bName.includes("hook") || bName.includes("headline")) return 1;
          if (aName.includes("body")) return -1;
          if (bName.includes("body")) return 1;
          if (aName.includes("cta")) return -1;
          if (bName.includes("cta")) return 1;
          return 0;
        })
      );

      // Use the sorted variables for all subsequent operations
      setTemplateVariables(sortedVariables);

      // Check actor variables using sorted variables
      const actorVarExists = Object.values(sortedVariables).some(
        (v: any) =>
          v.name.toLowerCase().includes("avatar") ||
          v.name.toLowerCase().includes("actor") ||
          v.name.toLowerCase().includes("character")
      );
      setHasActorVariables(actorVarExists);

      // Track found script variables
      const foundScriptVariables = {
        headline: false,
        body: false,
        cta: false,
      };

      // Initialize values using sorted variables
      const initialValues: VariableValues = {};
      Object.entries(sortedVariables).forEach(([key, variable]: [string, any]) => {
        const lowerName = variable.name.toLowerCase();

        // Start with clean copy
        initialValues[key] = JSON.parse(JSON.stringify(variable));

        // Handle script content
        if (lowerName.includes("headline") || lowerName.includes("hook")) {
          if (currentScript?.headline) {
            initialValues[key].properties.content = currentScript.headline;
            foundScriptVariables.headline = true;
          }
        } else if (lowerName.includes("body")) {
          if (currentScript?.body) {
            initialValues[key].properties.content = currentScript.body;
            foundScriptVariables.body = true;
          }
        } else if (lowerName.includes("cta")) {
          if (currentScript?.cta) {
            initialValues[key].properties.content = currentScript.cta;
            foundScriptVariables.cta = true;
          }
        }

        // Handle media variables
        if (variable.type === "image") {
          initialValues[key].properties = {
            url: variable.properties.url || null,
            asset_id: null,
            fit: "contain",
          };
        } else if (variable.type === "video") {
          initialValues[key].properties = {
            url: variable.properties.url || null,
            asset_id: null,
            play_style: "loop",
            fit: "contain",
          };
        }

        // Handle actor variables
        if (
          (lowerName.includes("actor") ||
            lowerName.includes("avatar") ||
            lowerName.includes("character")) &&
          isSingleActorMode &&
          actors.length === 1
        ) {
          if (!actors[0].isTemplateActor) {
            initialValues[key].properties = {
              ...initialValues[key].properties,
              character_id: actors[0].avatar_id,
              type: "avatar",
              ...(!initialValues[key].properties.content && {
                content: actors[0].avatar_id,
              }),
              ...(!initialValues[key].properties.asset_id && {
                asset_id: actors[0].avatar_id,
              }),
            };
          } else if (actors[0].heygenTemplate?.variables) {
            const actorVar = Object.entries(actors[0].heygenTemplate.variables).find(
              ([_, v]) => v.name?.toLowerCase() === variable.name?.toLowerCase()
            )?.[1];

            if (actorVar) {
              initialValues[key] = {
                ...initialValues[key],
                properties: {
                  ...initialValues[key].properties,
                  ...actorVar.properties,
                },
              };
            }
          }
        }
        if (isSingleActorMode && actors.length === 1 && actors[0].heygenTemplate?.variables) {
          const actorVariables = actors[0].heygenTemplate.variables;
          const matchingVarKey = Object.keys(actorVariables).find(
            (actorKey) =>
              actorVariables[actorKey].name?.toLowerCase() === variable.name?.toLowerCase()
          );

          if (matchingVarKey) {
            const actorVar = actorVariables[matchingVarKey];
            initialValues[key] = {
              ...initialValues[key],
              properties: {
                ...initialValues[key].properties,
                ...actorVar.properties,
              },
            };
            return;
          }
        }
      });

      // If there's no CTA variable in the template but the user has CTA content,
      // append it to the body variable to avoid losing the CTA text
      if (!foundScriptVariables.cta && currentScript?.cta && foundScriptVariables.body) {
        // Find the body variable key and append CTA to it
        const bodyKey = Object.keys(initialValues).find((key) => {
          const name = initialValues[key].name?.toLowerCase() || "";
          return name.includes("body");
        });

        if (bodyKey && initialValues[bodyKey].properties?.content) {
          initialValues[bodyKey].properties.content =
            initialValues[bodyKey].properties.content + "\n\n" + currentScript.cta;
        }
      }

      setMissingScriptVariables({
        showHeadline: !foundScriptVariables.headline,
        showBody: !foundScriptVariables.body,
        showCta: !foundScriptVariables.cta,
      });

      setVariableValues(initialValues);
    } catch (error) {
      console.error("Failed to fetch template variables:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleTemplateSelect = async (template: HeygenTemplate) => {
    setSelectedTemplate(template);
    await fetchTemplateVariables(template.template_id);
    setStep(1); // Move to variables configuration step
  };

  const fetchGuidelines = async (templateId: string) => {
    try {
      setLoadingGuidelines(true);
      const response = await getTemplateGuidelines(templateId);
      setGuidelines(response.data);
    } catch (error) {
      console.error("Failed to fetch template guidelines:", error);
      setGuidelines({
        template_id: templateId,
        title: `How to use ${selectedTemplate?.name}`,
        description: "No specific guidelines available for this template.",
      });
    } finally {
      setLoadingGuidelines(false);
    }
  };

  const handleOpenGuidelines = async () => {
    if (!selectedTemplate) return;
    await fetchGuidelines(selectedTemplate.template_id);
    setGuidelinesOpen(true);
  };

  const handleImageUpload = async (file: File) => {
    if (!currentImageVariable) return;

    const asset = await uploadBackgroundAsset(
      file,
      user?._id || "",
      workspaceId,
      undefined,
      videoPageId
    );

    setVariableValues((prev) => ({
      ...prev,
      [currentImageVariable]: {
        ...prev[currentImageVariable],
        properties: {
          ...prev[currentImageVariable].properties,
          url: asset.url,
          asset_id: asset.assetId,
        },
      },
    }));
  };

  const handleAssetSelect = (asset: any) => {
    if (!currentImageVariable) return;

    setVariableValues((prev) => ({
      ...prev,
      [currentImageVariable]: {
        ...prev[currentImageVariable],
        properties: {
          ...prev[currentImageVariable].properties,
          url: asset.url,
          asset_id: asset.assetId || asset._id,
        },
      },
    }));
    setAssetLibraryOpen(false);
  };

  const handleVariableChange = (variableName: string, value: any) => {
    setVariableValues((prev) => ({
      ...prev,
      [variableName]: {
        ...prev[variableName], // Preserve existing structure
        properties: {
          ...prev[variableName]?.properties, // Preserve existing properties
          content: typeof value === "object" ? value : value, // Update content or the whole value
        },
      },
    }));
  };

  // Validation function to check if all required fields are filled
  const areAllFieldsFilled = () => {
    // Check script fields if they exist in the template
    const hasHeadlineField = Object.values(templateVariables).some(
      (v: any) => v.name.toLowerCase().includes("headline") || v.name.toLowerCase().includes("hook")
    );
    const hasBodyField = Object.values(templateVariables).some((v: any) =>
      v.name.toLowerCase().includes("body")
    );
    const hasCtaField = Object.values(templateVariables).some((v: any) =>
      v.name.toLowerCase().includes("cta")
    );

    if (hasHeadlineField && !currentScript?.headline) return false;
    if (hasBodyField && !currentScript?.body) return false;
    if (hasCtaField && !currentScript?.cta) return false;

    // Check all other template variables
    for (const [key, variable] of Object.entries(templateVariables)) {
      const lowerName = (variable as any).name.toLowerCase();
      const varType = (variable as any).type;

      // Skip script variables, actor variables, and voice variables (already checked or handled separately)
      if (
        lowerName.includes("script") ||
        lowerName.includes("actor") ||
        lowerName.includes("avatar") ||
        lowerName.includes("character") ||
        lowerName.includes("headline") ||
        lowerName.includes("hook") ||
        lowerName.includes("body") ||
        lowerName.includes("cta") ||
        lowerName.includes("voice") ||
        varType === "voice"
      ) {
        continue;
      }

      const varValue = variableValues[key];

      // Check based on variable type
      if (varType === "text") {
        const content = varValue?.properties?.content;
        if (!content || content.trim() === "") return false;
      } else if (varType === "image" || varType === "video") {
        const url = varValue?.properties?.url;
        if (!url) return false;
      }
    }

    return true;
  };

  const handleAddTemplate = async () => {
    // Prevent double execution
    if (!selectedTemplate || applying) return;

    setApplying(true);
    try {
      const newTemplateActor = await registerTemplateActor(
        workspaceId,
        videoPageId,
        {
          template_id: selectedTemplate.template_id,
          name: selectedTemplate?.title || "name not available",
          thumbnail_image_url: selectedTemplate.thumbnail_image_url,
          aspect_ratio: selectedTemplate.aspect_ratio,
        },
        variableValues // Already in correct format
      );

      onTemplateAdded?.(newTemplateActor);
      onClose();
    } catch (error) {
      console.error("Failed to add template:", error);
    } finally {
      setApplying(false);
    }
  };

  const renderVariableInput = (variableName: string, variable: any) => {
    const lowerName = variable.name.toLowerCase();

    // Skip script variables entirely
    if (lowerName.includes("script")) {
      return null;
    }

    // Skip actor variables (we'll handle them with the checkbox)
    if (
      lowerName.includes("actor") ||
      lowerName.includes("avatar") ||
      lowerName.includes("character")
    ) {
      return null;
    }

    // Skip voice variables (handled by VoicePlayer)
    if (variable.type === "voice" || lowerName.includes("voice")) {
      return null;
    }

    // Handle script content variables specially
    if (lowerName.includes("headline") || lowerName.includes("hook")) {
      return (
        <WhiteTextField
          fullWidth
          label={variable.name}
          value={currentScript?.headline || ""}
          onChange={(e) => updateScriptContent({ headline: e.target.value })}
          margin="normal"
          sx={{
            "& .MuiInputLabel-root": {
              color: THEME_COLORS.black,
            },
            "& .MuiInputLabel-root.Mui-focused": {
              color: THEME_COLORS.yellow,
            },
          }}
        />
      );
    }

    if (lowerName.includes("body")) {
      return (
        <WhiteTextField
          fullWidth
          multiline
          rows={4}
          label={variable.name}
          value={currentScript?.body || ""}
          onChange={(e) => updateScriptContent({ body: e.target.value })}
          margin="normal"
          sx={{
            "& .MuiInputLabel-root": {
              color: THEME_COLORS.black,
            },
            "& .MuiInputLabel-root.Mui-focused": {
              color: THEME_COLORS.yellow,
            },
          }}
        />
      );
    }

    if (lowerName.includes("cta")) {
      return (
        <WhiteTextField
          fullWidth
          label={variable.name}
          value={currentScript?.cta || ""}
          onChange={(e) => updateScriptContent({ cta: e.target.value })}
          margin="normal"
          sx={{
            "& .MuiInputLabel-root": {
              color: THEME_COLORS.black,
            },
            "& .MuiInputLabel-root.Mui-focused": {
              color: THEME_COLORS.yellow,
            },
          }}
        />
      );
    }

    // Default variable handling
    const value =
      variableValues[variableName]?.properties?.content ||
      variableValues[variableName]?.properties?.url ||
      variableValues[variableName]?.properties?.asset_id ||
      "";

    switch (variable.type) {
      case "text":
        return (
          <WhiteTextField
            fullWidth
            label={variable.name}
            value={value}
            onChange={(e) => handleVariableChange(variableName, e.target.value)}
            margin="normal"
            sx={{
              "& .MuiInputLabel-root": {
                color: THEME_COLORS.black,
              },
              "& .MuiInputLabel-root.Mui-focused": {
                color: THEME_COLORS.yellow,
              },
            }}
          />
        );
      case "voice":
        return (
          <FormControl fullWidth margin="normal">
            <InputLabel>{variable.name}</InputLabel>
            <Select
              value={value}
              label={variable.name}
              onChange={(e) => handleVariableChange(variableName, { voice_id: e.target.value })}
            >
              <MenuItem value="voice1">Voice 1</MenuItem>
              <MenuItem value="voice2">Voice 2</MenuItem>
            </Select>
          </FormControl>
        );
      case "video":
        return (
          <Box sx={{ mt: 2, mb: 2 }}>
            {variableValues[variableName]?.properties?.url ? (
              <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                <video
                  src={variableValues[variableName].properties.url}
                  style={{
                    width: "90px",
                    height: "160px",
                    objectFit: "cover",
                    borderRadius: "4px",
                    backgroundColor: "#000",
                  }}
                  controls
                />
                <IconButton
                  sx={{
                    width: "35px",
                    height: "35px",
                    backgroundColor: "rgba(42, 42, 33, 0.5)",
                    color: "white",
                    "&:hover": {
                      backgroundColor: "rgba(0,0,0,0.7)",
                    },
                  }}
                  onClick={() => {
                    setCurrentImageVariable(variableName);
                    setMediaUploadModalOpen(true);
                    setMediaType("video");
                  }}
                >
                  <EditIcon />
                </IconButton>
              </Box>
            ) : (
              <Box sx={{ display: "flex", gap: 1, mt: 1 }}>
                <Button
                  variant="outlined"
                  startIcon={<MovieIcon />}
                  onClick={() => {
                    setCurrentImageVariable(variableName);
                    setMediaUploadModalOpen(true);
                    setMediaType("video");
                  }}
                >
                  Upload Video
                </Button>
                <Button
                  variant="outlined"
                  startIcon={<CollectionsIcon />}
                  onClick={() => {
                    setCurrentImageVariable(variableName);
                    setMediaType("video");
                    setAssetLibraryOpen(true);
                  }}
                  sx={{
                    borderColor: THEME_COLORS.yellow,
                    color: THEME_COLORS.darkYellow,
                    "&:hover": {
                      borderColor: THEME_COLORS.darkYellow,
                      backgroundColor: "rgba(255, 191, 0, 0.1)",
                    },
                  }}
                >
                  From Library
                </Button>
              </Box>
            )}
            <Typography variant="caption" display="block" sx={{ mt: 1 }}>
              {variable.name}
            </Typography>
          </Box>
        );
      case "image":
        return (
          <Box sx={{ mt: 2, mb: 2 }}>
            {variableValues[variableName]?.properties?.url ? (
              <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                <img
                  src={variableValues[variableName].properties.url}
                  alt={variable.name}
                  style={{
                    maxWidth: "100%",
                    maxHeight: "200px",
                    borderRadius: "4px",
                    objectFit: "contain",
                  }}
                />

                <IconButton
                  sx={{
                    width: "35px",
                    height: "35px",
                    backgroundColor: "rgba(42, 42, 33, 0.5)",
                    color: "white",
                    "&:hover": {
                      backgroundColor: "rgba(0,0,0,0.7)",
                    },
                  }}
                  onClick={() => {
                    setCurrentImageVariable(variableName);
                    setMediaUploadModalOpen(true);
                    setMediaType("image");
                  }}
                >
                  <EditIcon />
                </IconButton>
              </Box>
            ) : (
              <Box sx={{ display: "flex", gap: 1, mt: 1 }}>
                <Button
                  variant="outlined"
                  startIcon={<FileUploadIcon />}
                  onClick={() => {
                    setCurrentImageVariable(variableName);
                    setMediaUploadModalOpen(true);
                    setMediaType("image");
                  }}
                >
                  Upload Image
                </Button>
                <Button
                  variant="outlined"
                  startIcon={<CollectionsIcon />}
                  onClick={() => {
                    setCurrentImageVariable(variableName);
                    setMediaType("image");
                    setAssetLibraryOpen(true);
                  }}
                  sx={{
                    borderColor: THEME_COLORS.yellow,
                    color: THEME_COLORS.darkYellow,
                    "&:hover": {
                      borderColor: THEME_COLORS.darkYellow,
                      backgroundColor: "rgba(255, 191, 0, 0.1)",
                    },
                  }}
                >
                  From Library
                </Button>
              </Box>
            )}
            <Typography variant="caption" display="block" sx={{ mt: 1 }}>
              {variable.name}
            </Typography>
          </Box>
        );
      default:
        return (
          <WhiteTextField
            fullWidth
            label={variable.name}
            value={value}
            onChange={(e) => handleVariableChange(variableName, e.target.value)}
            margin="normal"
            sx={{
              "& .MuiInputLabel-root": {
                color: THEME_COLORS.black,
              },
              "& .MuiInputLabel-root.Mui-focused": {
                color: THEME_COLORS.yellow,
              },
            }}
          />
        );
    }
  };

  const handleAddOrNext = async () => {
    // Prevent double execution
    if (!selectedTemplate || applying) return;

    // In single actor mode, we skip the actor selection step
    if (isSingleActorMode) {
      if (hasActorVariables) {
        // Check if template is compatible with actor
        const hasActorVariable = Object.values(templateVariables).some(
          (v: any) =>
            v.name.toLowerCase().includes("avatar") || v.name.toLowerCase().includes("actor")
        );

        if (!hasActorVariable) {
          alert(
            "This template is not compatible with the selected actor. Please choose a different template."
          );
          return;
        }

        // Proceed to apply template
        if (actors && actors.length > 0) {
          await handleApplyToActors();
        }
      } else {
        // No actor variables, just apply
        if (actors && actors.length > 0) {
          await handleApplyToActors();
        }
      }
    } else {
      // Original multi-actor logic
      if (useDefaultActor || !hasActorVariables) {
        await handleAddTemplate();
      } else {
        setPendingTemplateApplication(true);
        setIsActorsDialogOpen(true);
      }
    }
  };

  // Add this handler for when actors are selected in the dialog
  const handleActorsSelected = async (selectedActorsFromDialog: Actor[]) => {
    // Update the selected actors with ALL actors from dialog
    setSelectedActors(selectedActorsFromDialog);
    setIsActorsDialogOpen(false);

    // Apply template to ALL selected actors
    if (pendingTemplateApplication && selectedTemplate) {
      // Call handleApplyToActors with the selected actors
      await handleApplyToActors(selectedActorsFromDialog);
      setPendingTemplateApplication(false);
    }
  };

  // Helper function to check if a variable is an actor/avatar/character variable
  const isActorVariable = (variable: any): boolean => {
    // Check by type first (HeyGen uses type: "avatar" or "character")
    if (variable.type === "avatar" || variable.type === "character") {
      return true;
    }
    // Also check by name as fallback
    const variableName = variable.name;
    if (!variableName) return false;
    const lowerName = variableName.toLowerCase();
    return (
      lowerName.includes("actor") || lowerName.includes("avatar") || lowerName.includes("character")
    );
  };

  // Helper function to update actor variables with the correct character_id for a specific actor
  const updateActorVariablesForActor = (
    variables: VariableValues,
    actor: Actor
  ): VariableValues => {
    const updatedVariables: VariableValues = JSON.parse(JSON.stringify(variables));

    Object.entries(updatedVariables).forEach(([key, variable]: [string, any]) => {
      if (isActorVariable(variable)) {
        // Update the character_id to use the actual actor's avatar_id
        updatedVariables[key].properties = {
          ...updatedVariables[key].properties,
          character_id: actor.avatar_id,
          type: "avatar",
        };
      }
    });

    return updatedVariables;
  };

  const handleApplyToActors = async (actorsToApply: Actor[] = selectedActors) => {
    // Prevent double execution
    if (!selectedTemplate || !onApplyTemplate || applying) return;

    setApplying(true);
    try {
      // Prepare per-actor variables map using avatar_id as key (since _id may not exist for unregistered actors)
      const perActorVariables: { [avatarId: string]: VariableValues } = {};

      for (const actor of actorsToApply) {
        if (actor.isTemplateActor) {
          // Template actors already have their variables set
          perActorVariables[actor.avatar_id] = variableValues;
        } else {
          // For regular actors, set their avatar_id as the character_id
          perActorVariables[actor.avatar_id] = updateActorVariablesForActor(variableValues, actor);
        }
      }

      const actorIds = actorsToApply.map((actor) => actor._id || actor.avatar_id);

      // Call onApplyTemplate once with all actors and their per-actor variables (keyed by avatar_id)
      await onApplyTemplate(
        selectedTemplate.template_id,
        actorIds,
        variableValues, // Base variables (for backwards compatibility)
        actorsToApply, // All actors
        perActorVariables // Per-actor variables with correct character_id (keyed by avatar_id)
      );
      onClose();
    } catch (error) {
      console.error("Failed to apply template:", error);
    } finally {
      setApplying(false);
    }
  };

  const renderStepIndicator = () => {
    const totalSteps = hasActorVariables && !isSingleActorMode ? 3 : 2;
    return (
      <Box display="flex" justifyContent="center" mt={2}>
        {Array.from({ length: totalSteps }).map((_, index) => (
          <Box
            key={index}
            sx={{
              width: "8px",
              height: "8px",
              borderRadius: "50%",
              bgcolor: step === index ? "#FFBF00" : "grey.500",
              mx: 1,
            }}
          />
        ))}
      </Box>
    );
  };

  return (
    <Modal open={open} onClose={onClose}>
      <>
        <Box sx={modalStyle}>
          <DialogTitle
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <Typography variant="h6">
              {step === 0 ? "Select Template" : "Configure Variables"}
            </Typography>
            <IconButton onClick={onClose}>
              <CloseIcon />
            </IconButton>
          </DialogTitle>

          <Divider />

          <DialogContent sx={{ flex: 1, overflow: "auto" }}>
            {step === 0 && (
              <>
                <Typography variant="body1" mb={2}>
                  Choose a template to continue
                </Typography>

                {loading ? (
                  <Box display="flex" justifyContent="center" py={4}>
                    <CircularProgress />
                  </Box>
                ) : (
                  <Box
                    display="grid"
                    gridTemplateColumns="repeat(auto-fill, minmax(200px, 1fr))"
                    gap={2}
                    sx={{ overflowY: "auto", maxHeight: "400px" }}
                  >
                    {templates
                      .filter((template) => !template.name.toLowerCase().includes("untitled video"))
                      .map((template) => (
                        <Box
                          key={template.template_id}
                          sx={{
                            position: "relative",
                            border:
                              selectedTemplate?.template_id === template.template_id
                                ? "2px solid #FFBF00"
                                : "1px solid #ddd",
                            borderRadius: "8px",
                            overflow: "hidden",
                            cursor: "pointer",
                            boxShadow: 2,
                            transition: "transform 0.2s ease-in-out",
                            "&:hover": {
                              borderColor: "#FFBF00",
                              transform: "scale(1.02)",
                            },
                          }}
                          onClick={() => handleTemplateSelect(template)}
                        >
                          {/* Template Image: thumbnail, fallback from guidelines, or placeholder */}
                          {template.thumbnail_image_url ? (
                            <Box
                              component="img"
                              src={template.thumbnail_image_url}
                              alt={template.name}
                              sx={{
                                width: "100%",
                                height: "200px",
                                objectFit: "scale-down",
                                display: "block",
                              }}
                            />
                          ) : templateFallbackImages[template.template_id] ? (
                            <Box
                              component="img"
                              src={templateFallbackImages[template.template_id]}
                              alt={template.name}
                              sx={{
                                width: "100%",
                                height: "200px",
                                objectFit: "scale-down",
                                display: "block",
                              }}
                            />
                          ) : (
                            <Box
                              sx={{
                                width: "100%",
                                height: "200px",
                                backgroundColor: "#e0e0e0",
                                display: "flex",
                                flexDirection: "column",
                                alignItems: "center",
                                justifyContent: "center",
                                gap: 1,
                              }}
                            >
                              <Typography
                                variant="subtitle1"
                                sx={{
                                  fontWeight: 600,
                                  color: "#666",
                                  textAlign: "center",
                                  px: 2,
                                }}
                              >
                                {template.name}
                              </Typography>
                              <Typography
                                variant="caption"
                                sx={{
                                  color: "#999",
                                  fontStyle: "italic",
                                }}
                              >
                                (Image preview unavailable)
                              </Typography>
                            </Box>
                          )}

                          {/* Overlay with name */}
                          <Box
                            sx={{
                              position: "absolute",
                              bottom: 0,
                              width: "100%",
                              background: "rgba(0, 0, 0, 0.5)",
                              color: "#fff",
                              textAlign: "center",
                              py: 1,
                            }}
                          >
                            <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                              {template.name}
                            </Typography>
                            <Typography variant="caption" sx={{ opacity: 0.8 }}>
                              {template.aspect_ratio}
                            </Typography>
                          </Box>
                        </Box>
                      ))}
                  </Box>
                )}
              </>
            )}
            {step === 1 && selectedTemplate && (
              <>
                <Typography variant="h6" mb={2}>
                  Configure Variables for {selectedTemplate.name}
                </Typography>

                {loading ? (
                  <Box display="flex" justifyContent="center">
                    <CircularProgress />
                  </Box>
                ) : (
                  <Box
                    sx={{
                      display: "flex",
                      gap: 4,
                      flexDirection: { xs: "column", md: "row" },
                    }}
                  >
                    {/* Left: Form Inputs */}
                    <Box sx={{ flex: 2, maxHeight: "400px", overflowY: "auto" }}>
                      {/* Info message about actor selection */}
                      {!isSingleActorMode && hasActorVariables && (
                        <Box
                          mb={3}
                          sx={{
                            p: 2,
                            backgroundColor: "rgba(255, 191, 0, 0.1)",
                            borderRadius: 1,
                            border: "1px solid rgba(255, 191, 0, 0.3)",
                          }}
                        >
                          <Typography
                            variant="body2"
                            sx={{
                              color: THEME_COLORS.darkGray,
                              fontStyle: "italic",
                            }}
                          >
                            In the next step you will be able to choose the actors you want to use
                            with this template.
                          </Typography>
                        </Box>
                      )}

                      {Object.entries(templateVariables).map(([name, variable]) => (
                        <Box key={name}>{renderVariableInput(name, variable)}</Box>
                      ))}
                    </Box>

                    {/* Right: Thumbnail and Explanation */}
                    <Box sx={{ flex: 1, textAlign: "center" }}>
                      <Button
                        variant="contained"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleOpenGuidelines();
                        }}
                        startIcon={<InfoIcon />}
                        sx={{
                          backgroundColor: "rgba(0,0,0,0.7)",
                          color: "white",
                          "&:hover": {
                            backgroundColor: "rgba(0,0,0,0.9)",
                          },
                          fontSize: "0.7rem",
                          padding: "4px 8px",
                          marginBottom: "10px",
                        }}
                      >
                        HOW TO USE THIS TEMPLATE
                      </Button>
                      {selectedTemplate.thumbnail_image_url ? (
                        <Box
                          component="img"
                          src={selectedTemplate.thumbnail_image_url}
                          alt="Template thumbnail"
                          sx={{
                            width: "50%",
                            borderRadius: 2,
                            boxShadow: 1,
                            objectFit: "cover",
                            mb: 2,
                          }}
                        />
                      ) : templateFallbackImages[selectedTemplate.template_id] ? (
                        <Box
                          component="img"
                          src={templateFallbackImages[selectedTemplate.template_id]}
                          alt="Template thumbnail"
                          sx={{
                            width: "50%",
                            borderRadius: 2,
                            boxShadow: 1,
                            objectFit: "cover",
                            mb: 2,
                          }}
                        />
                      ) : (
                        <Box
                          sx={{
                            width: "50%",
                            aspectRatio: "16/9",
                            backgroundColor: "#e0e0e0",
                            borderRadius: 2,
                            boxShadow: 1,
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "center",
                            justifyContent: "center",
                            mb: 2,
                            gap: 1,
                          }}
                        >
                          <Typography
                            variant="subtitle2"
                            sx={{
                              fontWeight: 600,
                              color: "#666",
                              textAlign: "center",
                              px: 2,
                            }}
                          >
                            {selectedTemplate.name}
                          </Typography>
                          <Typography
                            variant="caption"
                            sx={{
                              color: "#999",
                              fontStyle: "italic",
                            }}
                          >
                            (Preview unavailable)
                          </Typography>
                        </Box>
                      )}
                      <Typography
                        variant="body2"
                        color={THEME_COLORS.darkGray}
                        sx={{ fontSize: "12px" }}
                      >
                        This is not a live preview. The values you modify here will replace the
                        default content in the final video.
                      </Typography>
                    </Box>
                  </Box>
                )}
              </>
            )}
            {step === 2 && (
              <>
                <Typography variant="h6" mb={2}>
                  Select Actors to Apply Template
                </Typography>

                <Box
                  sx={{
                    maxHeight: "400px",
                    overflowY: "auto",
                    display: "flex",
                    flexDirection: "column",
                    gap: 1,
                  }}
                >
                  {actors?.map((actor) => (
                    <Box
                      key={actor._id}
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 2,
                        p: 1,
                        borderRadius: 1,
                        "&:hover": {
                          backgroundColor: "action.hover",
                        },
                      }}
                    >
                      <Checkbox
                        checked={selectedActors.some((a) => a._id === actor._id)}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setSelectedActors((prev) => [...prev, actor]);
                          } else {
                            setSelectedActors((prev) => prev.filter((a) => a._id !== actor._id));
                          }
                        }}
                        sx={{
                          color: THEME_COLORS.yellow,
                          "&.Mui-checked": {
                            color: THEME_COLORS.yellow,
                          },
                        }}
                      />
                      <Box
                        sx={{
                          width: 50,
                          height: 50,
                          borderRadius: "50%",
                          overflow: "hidden",
                          flexShrink: 0,
                        }}
                      >
                        {actor.preview_image_url ? (
                          <img
                            src={actor.preview_image_url}
                            alt=""
                            style={{
                              width: "100%",
                              height: "100%",
                              objectFit: "cover",
                            }}
                          />
                        ) : (
                          <Box
                            sx={{
                              width: "100%",
                              height: "100%",
                              backgroundColor: THEME_COLORS.lightGray,
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                            }}
                          >
                            <Typography variant="body2">No Image</Typography>
                          </Box>
                        )}
                      </Box>
                    </Box>
                  ))}
                </Box>
              </>
            )}{" "}
            <MediaUploadModal
              open={imageUploadModalOpen}
              onClose={() => setMediaUploadModalOpen(false)}
              onUpload={handleImageUpload}
              onAssetSelect={handleAssetSelect}
              type={mediaType}
            />
            <AssetLibraryModal
              open={assetLibraryOpen}
              onClose={() => setAssetLibraryOpen(false)}
              mode="select"
              onSelect={handleAssetSelect}
              filterType={mediaType === "video" ? "video" : "image"}
              title={`Select ${mediaType === "video" ? "Video" : "Image"} from Library`}
            />
            {selectedTemplate && guidelines && (
              <TemplateGuidelinesDialog
                open={guidelinesOpen}
                onClose={() => setGuidelinesOpen(false)}
                guidelines={guidelines}
              />
            )}
          </DialogContent>

          {renderStepIndicator()}

          <DialogActions sx={{ p: 2 }}>
            {step > 0 && (
              <Button onClick={() => setStep(step - 1)} sx={{ mr: "auto" }}>
                Back
              </Button>
            )}

            {step === 0 ? (
              <Button
                variant="contained"
                onClick={() => setStep(1)}
                disabled={!selectedTemplate}
                sx={{ backgroundColor: "#FFBF00", color: "#000" }}
              >
                Next
              </Button>
            ) : step === 1 ? (
              <>
                {!areAllFieldsFilled() && (
                  <Typography
                    variant="caption"
                    sx={{
                      color: "error.main",
                      mr: 2,
                      fontStyle: "italic",
                    }}
                  >
                    Please fill in all required fields
                  </Typography>
                )}
                <Button
                  variant="contained"
                  onClick={handleAddOrNext}
                  disabled={applying || !areAllFieldsFilled()}
                  sx={{ backgroundColor: "#FFBF00", color: "#000" }}
                >
                  {applying ? (
                    <CircularProgress size={24} />
                  ) : useDefaultActor || !hasActorVariables ? (
                    "Add Template"
                  ) : (
                    "Next"
                  )}
                </Button>
              </>
            ) : (
              <Button
                variant="contained"
                onClick={() => handleApplyToActors()}
                disabled={applying || selectedActors.length === 0}
                sx={{ backgroundColor: "#FFBF00", color: "#000" }}
              >
                {applying ? <CircularProgress size={24} /> : "Apply Template"}
              </Button>
            )}
          </DialogActions>
        </Box>

        <ActorsDialog
          open={isActorsDialogOpen}
          actors={actors} // All available actors
          onClose={() => {
            setIsActorsDialogOpen(false);
            setPendingTemplateApplication(false);
          }}
          onSelectActors={handleActorsSelected}
          selectedActors={selectedActors}
          avatarLabActors={[]}
          onSelectAvatarLabActors={() => {}}
          mode="template" // Use template mode for fresh selection
        />
      </>
    </Modal>
  );
};

export default TemplateDialog;
